import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import api from 'src/app/api';
import type { RootState } from 'src/store/reducer';
import { MfaExchangeResponse, MfaFailureResult, MfaStatusResponse, StepUpMfaArgs, StepUpMfaResult } from './types';
import { userManager, userMfaManager } from 'src/userManager';

export const MFA_LOCKED_MESSAGE = 'Too many failed verification attempts. Please contact your administrator or try again later.';
export const MFA_CANCELLED_MESSAGE = 'Verification was cancelled. Please try again.';
export const MFA_FAILED_MESSAGE = 'Verification failed. The MFA token could not be validated.';

export enum MfaActionTypes {
  PerformStepUp = '@@common/mfa/PerformStepUp',
}

export const performStepUpMfa = createAsyncThunk<StepUpMfaResult, StepUpMfaArgs, { state: RootState; rejectValue: MfaFailureResult }>(
  MfaActionTypes.PerformStepUp,
  async ({ service, actionType }, thunkApi) => {
    if (thunkApi.getState().mfa.attemptsRemaining === 0) {
      return thunkApi.rejectWithValue({ error: MFA_LOCKED_MESSAGE, attemptsRemaining: 0, isLocked: true, challengeId: null });
    }

    if (actionType === 'window') {
      const existing = thunkApi.getState().mfa.mfaUserId ?? (await userManager.getUser().catch(() => null))?.profile?.sub ?? null;
      const status = await fetchMfaStatus(service, existing).catch(() => null);
      if (status?.valid && status.userId) {
        return { userId: status.userId, service };
      }
    }

    const challengeId = thunkApi.getState().mfa.challengeId ?? uuidv4();
    const attemptsRemaining = thunkApi.getState().mfa.attemptsRemaining;

    let accessToken: string;
    try {
      accessToken = await triggerMfaPopup();
    } catch {
      return thunkApi.rejectWithValue({ error: MFA_CANCELLED_MESSAGE, attemptsRemaining, isLocked: false, challengeId });
    }

    let data: MfaExchangeResponse;
    try {
      ({ data } = await api.post<MfaExchangeResponse>(
        getMfaEndpoint(service, 'exchange'),
        { mfaToken: accessToken, actionType, actionId: challengeId },
        { headers: { Authorization: `Bearer ${accessToken}` }, skipAuthOverride: true }
      ));
    } catch {
      return thunkApi.rejectWithValue({ error: MFA_FAILED_MESSAGE, attemptsRemaining, isLocked: false, challengeId });
    }

    if (!data.success || !data.userId) {
      const remaining = data.attemptsRemaining ?? attemptsRemaining;
      const isLocked = data.error === 'MFA_LOCKED' || remaining === 0;
      return thunkApi.rejectWithValue({
        error: isLocked ? MFA_LOCKED_MESSAGE : data.error || MFA_FAILED_MESSAGE,
        attemptsRemaining: isLocked ? 0 : remaining,
        isLocked,
        challengeId: isLocked ? null : challengeId,
      });
    }

    return { userId: data.userId, service };
  }
);

export const fetchMfaStatus = async (service: string, userId: string | null): Promise<MfaStatusResponse> => {
  if (!userId) {
    return { valid: false, actionType: null, expiresAt: null };
  }
  const { data } = await api.get<MfaStatusResponse>(getMfaEndpoint(service, 'status'), { params: { userId } });
  return data;
};

export const triggerMfaPopup = async (): Promise<string> => {
  const user = await userMfaManager.signinPopup({
    scope: 'openid profile allowedrisk:high',
  });
  return user.access_token;
};

export const getMfaEndpoint = (service: string, action: 'exchange' | 'status'): string => `/${service}/mfa/${action}`;
